import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import baseTheme from "../styles/theme";
import { authRuntimeConfig } from "../authConfig";
import { getAffiliateSlug, readCachedAffiliate, resolveAffiliate } from "./affiliateService";
import { createAffiliateTheme } from "./createAffiliateTheme";
import { AffiliateResolution, ResolvedAffiliate } from "./types";
import { useDocumentBranding } from "./useDocumentBranding";

/**
 * One lookup per page load, shared by everything that needs it: the sign-in gateway runs before the
 * app renders, and this provider brands the signed-in app. The result is cached in the module so a
 * remount reuses the in-flight request instead of starting another.
 */
const inFlight = new Map<string, Promise<AffiliateResolution>>();

export function resolveAffiliateOnce(slug: string | undefined): Promise<AffiliateResolution> {
  // Keyed, because the app brands from the URL alone while the sign-in pages also accept the
  // remembered slug; a single slot would make those two evict each other.
  const key = `${window.location.host}:${slug ?? ""}`;

  let pending = inFlight.get(key);
  if (!pending) {
    pending = resolveAffiliate({
      hostname: window.location.hostname,
      slug,
      apiUrl: authRuntimeConfig.apiUrl,
      cdnBaseUrl: authRuntimeConfig.brandingCdnBaseUrl,
      skipHosts: authRuntimeConfig.resolveSkipHosts,
    });
    inFlight.set(key, pending);
  }
  return pending;
}

/** Forces the next lookup to run again, e.g. after the user retries sign-in. */
export function clearAffiliateResolution(): void {
  inFlight.clear();
}

const AffiliateContext = createContext<ResolvedAffiliate | null>(null);

/** The affiliate this site is serving, or null for the main product. */
export function useAffiliate(): ResolvedAffiliate | null {
  return useContext(AffiliateContext);
}

/**
 * Applies affiliate identity (logo, name, favicon, title) and accent colours to the whole app.
 * Page and card backgrounds keep the product palette, so data-dense screens stay readable; the
 * sign-in page applies the full palette separately.
 */
export function AffiliateProvider({ children }: { children: React.ReactNode }) {
  // Hostname, or a slug in the address bar right now — never a slug left over from earlier.
  const urlSlug = getAffiliateSlug(window.location.search, { includeStored: false });

  // Seeded from the last known branding so the first paint is already branded; the lookup below
  // confirms or replaces it.
  const [affiliate, setAffiliate] = useState<ResolvedAffiliate | null>(() =>
    readCachedAffiliate(window.location.hostname, urlSlug, authRuntimeConfig.brandingCdnBaseUrl)
  );

  useEffect(() => {
    let active = true;

    resolveAffiliateOnce(urlSlug)
      .then((resolution) => {
        if (active) {
          setAffiliate(resolution.kind === "found" ? resolution.affiliate : null);
        }
      })
      .catch(() => undefined);

    return () => {
      active = false;
    };
  }, [urlSlug]);

  useDocumentBranding(affiliate?.theme ?? null);

  const theme = useMemo(
    () => (affiliate ? createAffiliateTheme(baseTheme, affiliate.theme, { scope: "accents" }) : baseTheme),
    [affiliate]
  );

  return (
    <AffiliateContext.Provider value={affiliate}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </AffiliateContext.Provider>
  );
}
